import { PDFDocument, PageSizes, StandardFonts, rgb } from 'pdf-lib';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import mapListRaw from '../data/mapList';

const W = 1000;
const H = 600;
const MAX_STEPS = 10;
const MAX_THUMBS = 10;
const RATIO = W / H;

const esc = (v = '') => String(v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }[c]));
const norm = (v = '') => String(v).trim().toLowerCase().replace(/\s+/g, '_');
const pretty = (v = '') => String(v).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const toMillis = (v) => (v?.toMillis ? v.toMillis() : v?.toDate ? v.toDate().getTime() : new Date(v || 0).getTime() || 0);
const isEmptyStep = (s) => !['playersOnCanvas', 'utilitiesOnCanvas', 'drawnLines', 'textBoxes', 'imagesOnCanvas', 'visionCones'].some((k) => (s?.[k] || []).length);
const normalizeStep = (d = {}) => ({
  playersOnCanvas: d.playerPositions || d.playersOnCanvas || [],
  utilitiesOnCanvas: d.utilityPlacements || d.utilitiesOnCanvas || [],
  drawnLines: d.drawings || d.drawnLines || [],
  textBoxes: d.textBoxes || [],
  imagesOnCanvas: d.imagesOnCanvas || [],
  visionCones: d.visionCones || [],
});

const buildConePath = (origin, target, fovDeg, radius) => {
  const angle = Math.atan2((target?.y || 0) - (origin?.y || 0), (target?.x || 0) - (origin?.x || 0));
  const half = (fovDeg * Math.PI) / 180 / 2;
  const start = angle - half;
  const end = angle + half;
  const steps = Math.max(8, Math.floor((fovDeg || 60) / 5));
  const out = [`M ${origin?.x || 0} ${origin?.y || 0}`];
  for (let i = 0; i <= steps; i += 1) {
    const a = start + (end - start) * (i / steps);
    out.push(`${i === 0 ? 'L' : ''} ${(origin?.x || 0) + Math.cos(a) * (radius || 120)} ${(origin?.y || 0) + Math.sin(a) * (radius || 120)}`);
  }
  out.push('Z');
  return out.join(' ');
};

const findMapImage = (mapName, mode) => {
  const pool = mapListRaw[(mode || '').toLowerCase()] || [];
  const hit = pool.find((m) => norm(m.name) === norm(mapName) || String(m.name).toLowerCase() === String(mapName || '').toLowerCase());
  return hit?.image || '/assets/placeholder.jpg';
};

const toDataUrl = async (src, cache) => {
  if (!src) return '';
  if (src.startsWith('data:')) return src;
  const abs = new URL(src, window.location.origin).href;
  if (cache.has(abs)) return cache.get(abs);
  try {
    const res = await fetch(abs, { mode: 'cors' });
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
    cache.set(abs, dataUrl);
    return dataUrl;
  } catch (_) {
    return '';
  }
};

const svgToPng = async (svg) => {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise((resolve, reject) => {
      const node = new Image();
      node.onload = () => resolve(node);
      node.onerror = reject;
      node.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0b1020';
    ctx.fillRect(0, 0, W, H);
    ctx.drawImage(img, 0, 0, W, H);
    const png = await new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('png failed'))), 'image/png'));
    return new Uint8Array(await png.arrayBuffer());
  } finally {
    URL.revokeObjectURL(url);
  }
};

const buildStepSvg = async (strategy, step, cache) => {
  const mapSrc = await toDataUrl(findMapImage(strategy.map, strategy.mode), cache);
  const playerUrls = await Promise.all((step.playersOnCanvas || []).map((p) => toDataUrl(p.avatar || p.image || p.url, cache)));
  const utilityUrls = await Promise.all((step.utilitiesOnCanvas || []).map((u) => toDataUrl(u.src || `/assets/${u.type}.png`, cache)));
  const imageUrls = await Promise.all((step.imagesOnCanvas || []).map((i) => toDataUrl(i.src || i.url, cache)));
  const drawings = (step.drawnLines || []).map((line) => `<polyline points="${(line.points || []).map((p) => `${p.x},${p.y}`).join(' ')}" stroke="${line.color || '#fff'}" stroke-width="${line.thickness || 4}" stroke-opacity="${(line.opacity || 10) / 10}" fill="none" stroke-dasharray="${line.type === 'dotted' ? '4 4' : 'none'}" stroke-linecap="round" stroke-linejoin="round" marker-end="${String(line.type || '').includes('arrow') ? 'url(#arrow)' : ''}" />`).join('');
  const players = (step.playersOnCanvas || []).map((p, idx) => {
    const x = (Number(p.relX) / 100) * W;
    const y = (Number(p.relY) / 100) * H;
    const face = playerUrls[idx] || '';
    const fallback = esc((p.name || 'P').slice(0, 1).toUpperCase());
    return `<rect x="${x - 4}" y="${y - 4}" width="34" height="34" rx="6" fill="${p.side === 'enemy' ? '#d97706' : '#1e3a8a'}" opacity="0.85"/>${face ? `<image href="${face}" x="${x}" y="${y}" width="26" height="26" preserveAspectRatio="xMidYMid slice" />` : `<circle cx="${x + 13}" cy="${y + 13}" r="13" fill="#334155"/><text x="${x + 13}" y="${y + 18}" text-anchor="middle" fill="#fff" font-size="11" font-family="Arial">${fallback}</text>`}`;
  }).join('');
  const utils = (step.utilitiesOnCanvas || []).map((u, idx) => {
    const size = ['warning', 'anchor', 'flag', 'star'].includes(u.type) ? 32 : 45;
    const src = utilityUrls[idx] || '';
    return src ? `<image href="${src}" x="${(Number(u.relX) / 100) * W}" y="${(Number(u.relY) / 100) * H}" width="${size}" height="${size}" preserveAspectRatio="xMidYMid slice" />` : '';
  }).join('');
  const cones = (step.visionCones || []).map((c) => `<path d="${buildConePath(c.origin || { x: 0, y: 0 }, c.target || { x: 0, y: 0 }, c.fov || 60, c.radius || 120)}" fill="${c.color || '#fff'}" fill-opacity="0.08" />`).join('');
  const text = (step.textBoxes || []).map((tb) => {
    const x = (Number(tb.relX) / 100) * W;
    const y = (Number(tb.relY) / 100) * H;
    const w = Number(tb.width) || 140;
    const lines = String(tb.text || '').split('\n').slice(0, 8);
    return `<rect x="${x}" y="${y}" width="${w}" height="${Math.max(24, lines.length * 16 + 8)}" rx="6" fill="#1f1f1f" opacity="0.9"/>${lines.map((ln, i) => `<text x="${x + 8}" y="${y + 20 + i * 16}" fill="#fff" font-size="14" font-family="Arial">${esc(ln)}</text>`).join('')}`;
  }).join('');
  const imgs = (step.imagesOnCanvas || []).map((im, idx) => {
    const x = im.relX != null ? (Number(im.relX) / 100) * W : Number(im.x) || 0;
    const y = im.relY != null ? (Number(im.relY) / 100) * H : Number(im.y) || 0;
    const src = imageUrls[idx] || '';
    return src ? `<image href="${src}" x="${x}" y="${y}" width="${Number(im.width) || 150}" height="${Number(im.height) || 150}" preserveAspectRatio="xMidYMid slice" />` : '';
  }).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}"><defs><marker id="arrow" markerWidth="10" markerHeight="10" refX="6" refY="3" orient="auto" markerUnits="strokeWidth"><path d="M0,0 L0,6 L9,3 z" fill="#fff"/></marker></defs><rect x="0" y="0" width="${W}" height="${H}" fill="#0b1020"/>${mapSrc ? `<image href="${mapSrc}" x="0" y="0" width="${W}" height="${H}" preserveAspectRatio="xMidYMid meet" />` : ''}${cones}${drawings}${players}${utils}${text}${imgs}</svg>`;
};

const getStrategies = async (teamId, seasonId) => {
  const seasonPath = seasonId ? `teams/${teamId}/seasons/${seasonId}/strategies` : null;
  const season = seasonPath ? (await getDocs(collection(db, seasonPath))).docs.map((d) => ({ id: d.id, ...d.data() })) : [];
  const legacy = (await getDocs(collection(db, `teams/${teamId}/strategies`))).docs.map((d) => ({ id: d.id, ...d.data() }));
  return (season.length ? season : legacy).sort((a, b) => (toMillis(b.updatedAt) || toMillis(b.createdAt)) - (toMillis(a.updatedAt) || toMillis(a.createdAt)));
};

const loadSteps = async (teamId, seasonId, strategyId) => {
  const read = async (path) => (await getDocs(collection(db, path))).docs;
  const seasonDocs = seasonId ? await read(`teams/${teamId}/seasons/${seasonId}/strategies/${strategyId}/sequences`) : [];
  const docs = seasonDocs.length ? seasonDocs : await read(`teams/${teamId}/strategies/${strategyId}/sequences`);
  const out = {};
  docs.forEach((d) => { const n = Number.parseInt(d.id, 10); if (n >= 1 && n <= MAX_STEPS) out[n] = normalizeStep(d.data()); });
  for (let i = 1; i <= MAX_STEPS; i += 1) if (!out[i]) out[i] = normalizeStep({});
  return out;
};

const drawPageHeader = (page, width, height, margin, mapName, phase, fontBold) => {
  page.drawText(`${pretty(mapName)} - ${pretty(phase)} Playbook`, {
    x: margin,
    y: height - margin - 16,
    size: 14,
    font: fontBold,
    color: rgb(0.08, 0.12, 0.2),
  });
  page.drawLine({
    start: { x: margin, y: height - margin - 22 },
    end: { x: width - margin, y: height - margin - 22 },
    thickness: 0.7,
    color: rgb(0.82, 0.84, 0.88),
  });
};

const drawPlayHeader = (page, margin, y, play, phase, font, fontBold, continuation = false) => {
  const title = `${play.strategy.name || 'Untitled'}${continuation ? ' (cont.)' : ''}`;
  const meta = `${String(play.strategy.mode || '').toUpperCase()} | ${pretty(play.strategy.map || '')} | ${pretty(phase)} | ${play.count} ${play.count === 1 ? 'Step' : 'Steps'}`;
  page.drawText(title, {
    x: margin,
    y: y - 11,
    size: 11,
    font: fontBold,
    color: rgb(0.12, 0.12, 0.12),
  });
  page.drawText(meta, {
    x: margin,
    y: y - 22,
    size: 8,
    font,
    color: rgb(0.35, 0.38, 0.42),
  });
};

const drawStepBadge = (page, x, yTop, step, fontBold) => {
  page.drawRectangle({
    x: x + 5,
    y: yTop - 18,
    width: 18,
    height: 12,
    color: rgb(0.07, 0.09, 0.12),
    opacity: 0.85,
  });
  page.drawText(`S${step}`, {
    x: x + 8,
    y: yTop - 15,
    size: 7,
    font: fontBold,
    color: rgb(1, 1, 1),
  });
};

const renderThumbnailLayout = async ({ pdf, plays, mapName, phase, font, fontBold }) => {
  const pages = [];
  let cur = [];
  let used = 0;
  for (const p of plays) {
    if (used > 0 && used + p.count > MAX_THUMBS) { pages.push(cur); cur = []; used = 0; }
    cur.push(p);
    used += p.count;
  }
  if (cur.length) pages.push(cur);

  for (const group of pages) {
    const page = pdf.addPage(PageSizes.A4);
    const { width, height } = page.getSize();
    const margin = 14;
    const pageHeaderH = 30;
    const playHeaderH = 24;
    const blockGap = 8;
    const rowGap = 5;
    const colGap = 8;
    const sepH = 8;
    const totalRows = group.reduce((n, g) => n + Math.ceil(g.count / 2), 0);
    const maxW = (width - margin * 2 - colGap) / 2;
    const hByWidth = Math.floor(maxW / RATIO);
    const availH = height - margin * 2 - pageHeaderH - group.length * playHeaderH - (group.length - 1) * (blockGap + sepH) - (totalRows - group.length) * rowGap;
    const thumbH = Math.max(30, Math.min(hByWidth, Math.floor(availH / Math.max(1, totalRows))));
    const thumbW = thumbH * RATIO;
    let y = height - margin;

    drawPageHeader(page, width, height, margin, mapName, phase, fontBold);
    y -= pageHeaderH;

    for (let pIndex = 0; pIndex < group.length; pIndex += 1) {
      const play = group[pIndex];
      drawPlayHeader(page, margin, y, play, phase, font, fontBold, false);
      y -= playHeaderH;
      for (let i = 0; i < play.thumbs.length; i += 1) {
        const row = Math.floor(i / 2);
        const col = i % 2;
        const x = margin + col * (thumbW + colGap);
        const top = y - row * (thumbH + rowGap);
        const png = await pdf.embedPng(play.thumbs[i].bytes);
        page.drawImage(png, { x, y: top - thumbH, width: thumbW, height: thumbH });
        page.drawRectangle({
          x,
          y: top - thumbH,
          width: thumbW,
          height: thumbH,
          borderColor: rgb(0.78, 0.8, 0.84),
          borderWidth: 0.6,
        });
        drawStepBadge(page, x, top, play.thumbs[i].step, fontBold);
      }
      y -= Math.ceil(play.count / 2) * thumbH + (Math.ceil(play.count / 2) - 1) * rowGap + blockGap;
      if (pIndex < group.length - 1) {
        page.drawLine({
          start: { x: margin, y },
          end: { x: width - margin, y },
          thickness: 0.6,
          color: rgb(0.88, 0.89, 0.92),
        });
        y -= sepH;
      }
    }
  }
};

const renderFullWidthLayout = async ({
  pdf,
  plays,
  mapName,
  phase,
  font,
  fontBold,
  allowContinuationPages = true,
  keepPlayOnOnePage = false,
}) => {
  const margin = 14;
  const pageHeaderH = 30;
  const playHeaderH = 24;
  const stepGap = 8;
  const embedCache = new Map();

  const makePage = (play, continuation = false) => {
    const page = pdf.addPage(PageSizes.A4);
    const { width, height } = page.getSize();
    drawPageHeader(page, width, height, margin, mapName, phase, fontBold);
    drawPlayHeader(page, margin, height - margin - pageHeaderH, play, phase, font, fontBold, continuation);
    return {
      page,
      width,
      bottomY: margin,
      contentW: width - margin * 2,
      topY: height - margin - pageHeaderH - playHeaderH,
    };
  };

  const drawStep = async (ctx, play, thumb, yTop, scale = 1) => {
    const key = `${play.strategy.id}:${thumb.step}`;
    let png = embedCache.get(key);
    if (!png) {
      png = await pdf.embedPng(thumb.bytes);
      embedCache.set(key, png);
    }
    const w = ctx.contentW * scale;
    const h = (w / RATIO);
    const x = margin + (ctx.contentW - w) / 2;
    ctx.page.drawImage(png, { x, y: yTop - h, width: w, height: h });
    ctx.page.drawRectangle({
      x,
      y: yTop - h,
      width: w,
      height: h,
      borderColor: rgb(0.78, 0.8, 0.84),
      borderWidth: 0.6,
    });
    drawStepBadge(ctx.page, x, yTop, thumb.step, fontBold);
    return h;
  };

  for (const play of plays) {
    const singlePage = keepPlayOnOnePage || !allowContinuationPages;
    if (singlePage) {
      const ctx = makePage(play, false);
      const rawH = ctx.contentW / RATIO;
      const availableH = ctx.topY - ctx.bottomY;
      const totalRaw = play.count * rawH + Math.max(0, play.count - 1) * stepGap;
      const scale = totalRaw > 0 ? Math.min(1, availableH / totalRaw) : 1;
      let y = ctx.topY;
      for (const thumb of play.thumbs) {
        const h = await drawStep(ctx, play, thumb, y, scale);
        y -= h + stepGap;
      }
      continue;
    }

    let ctx = makePage(play, false);
    let y = ctx.topY;
    for (let i = 0; i < play.thumbs.length; i += 1) {
      const thumb = play.thumbs[i];
      const nextH = ctx.contentW / RATIO;
      if (y - nextH < ctx.bottomY && i > 0) {
        ctx = makePage(play, true);
        y = ctx.topY;
      }
      const fitScale = y - nextH < ctx.bottomY ? Math.max(0.2, (y - ctx.bottomY) / nextH) : 1;
      const h = await drawStep(ctx, play, thumb, y, fitScale);
      y -= h + stepGap;
    }
  }
};

export async function exportPlaybookPDF({
  teamId,
  seasonId,
  mapName,
  phase,
  skipEmptySteps = true,
  layout = 'thumbnails',
  allowContinuationPages = true,
  keepPlayOnOnePage = false,
  strategyIds = [],
}) {
  const selectedIds = Array.isArray(strategyIds) ? strategyIds.filter(Boolean) : [];
  const selectedIdSet = new Set(selectedIds);
  let strategies = await getStrategies(teamId, seasonId);

  if (selectedIds.length) {
    const selectedOrder = new Map(selectedIds.map((id, index) => [id, index]));
    strategies = strategies
      .filter((s) => selectedIdSet.has(s.id))
      .sort((a, b) => selectedOrder.get(a.id) - selectedOrder.get(b.id));
  } else {
    const mapKey = norm(mapName);
    const phaseKey = String(phase || '').toLowerCase();
    strategies = strategies.filter((s) => norm(s.map) === mapKey && (s.tags?.phases || []).some((p) => String(p).toLowerCase() === phaseKey));
  }

  if (!strategies.length) {
    throw new Error(selectedIds.length ? 'No selected plays found for export.' : 'No plays found for selected map and phase.');
  }

  const cache = new Map();
  const plays = [];
  for (const s of strategies) {
    const steps = await loadSteps(teamId, seasonId, s.id);
    let limit = MAX_STEPS;
    if (skipEmptySteps) {
      const last = [...Array(MAX_STEPS)].map((_, i) => i + 1).reverse().find((n) => !isEmptyStep(steps[n]));
      limit = Math.max(1, Math.min(MAX_STEPS, last || 1));
    }
    const indices = [...Array(limit)].map((_, i) => i + 1);
    const thumbs = [];
    for (const i of indices) thumbs.push({ step: i, bytes: await svgToPng(await buildStepSvg(s, steps[i], cache)) });
    plays.push({ strategy: s, thumbs, count: thumbs.length });
  }

  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const layoutKey = String(layout || 'thumbnails').toLowerCase();
  if (layoutKey === 'fullwidth' || layoutKey === 'full_width') {
    await renderFullWidthLayout({
      pdf,
      plays,
      mapName,
      phase,
      font,
      fontBold,
      allowContinuationPages,
      keepPlayOnOnePage,
    });
  } else {
    await renderThumbnailLayout({ pdf, plays, mapName, phase, font, fontBold });
  }

  return pdf.save();
}
